import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/app/Layout";
import { CheckCircle2, XCircle, Inbox, FileText, User, Calendar, DollarSign } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function VpReview() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [open, setOpen] = useState(null); // selected report detail
  const [decisionCtx, setDecisionCtx] = useState(null); // { report, decision }
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vp/reports-pending");
      setReports(data);
    } catch {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openReport = async (r) => {
    setOpen({ ...r, loading: true });
    try {
      const { data } = await api.get(`/vp/reports/${r.report_id}`);
      setOpen(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir");
      setOpen(null);
    }
  };

  const decide = async () => {
    const r = decisionCtx?.report;
    const decision = decisionCtx?.decision;
    if (!r || !decision) return;
    if (decision === "rejected" && !note.trim()) {
      toast.error("Une note explicative est requise pour un refus");
      return;
    }
    setActing(r.report_id);
    try {
      await api.post(`/vp/reports/${r.report_id}/decide`, {
        decision,
        note: note.trim() || null,
      });
      toast.success(decision === "approved" ? "Rapport approuvé" : "Rapport refusé");
      setDecisionCtx(null);
      setNote("");
      setOpen(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setActing(null);
    }
  };

  const downloadPdf = async (r) => {
    try {
      const res = await api.post("/export/pdf", { report_id: r.report_id }, { responseType: "blob", timeout: 180000 });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `factures_${r.name || "rapport"}.pdf`;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Téléchargement impossible");
    }
  };

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <p className="overline mb-2">Vice-président</p>
      <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-tight sm:leading-none mb-3">
        Rapports à approuver
      </h1>
      <p className="text-gray-500 mb-8 max-w-lg">
        Les rapports de dépenses transmis par l'administrateur. Approuvez ou refusez avec une note explicative.
      </p>

      {loading ? (
        <div className="ra-card p-8 text-center text-gray-400">Chargement...</div>
      ) : reports.length === 0 ? (
        <div className="ra-card p-12 text-center flex flex-col items-center gap-3">
          <Inbox size={40} className="text-gray-300" />
          <div className="font-heading font-black text-xl">Aucun rapport en attente</div>
          <div className="text-sm text-gray-500">Aucun compte de dépenses ne vous a été transmis pour approbation.</div>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="vp-reports-list">
          {reports.map((r) => (
            <div
              key={r.report_id}
              className="ra-card p-5 border-l-4 !border-l-[#002FA7]"
              data-testid={`vp-report-${r.report_id}`}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <div className="font-heading font-black text-lg tracking-tight">{r.name}</div>
                    <span className="badge !bg-blue-100 !text-blue-800">À approuver</span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {r.owner_name} · <span className="text-gray-500">{r.owner_email}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mt-2">
                    <span><strong className="num text-gray-900 dark:text-white">{r.invoice_count}</strong> facture(s)</span>
                    <span>Total : <strong className="num text-gray-900 dark:text-white">{(r.total_sum || 0).toFixed(2)} $</strong></span>
                    {r.month && <span>Mois : {r.month}</span>}
                    {r.sent_to_vp_at && <span>Reçu le {String(r.sent_to_vp_at).slice(0,10)}</span>}
                  </div>
                  {r.admin_note_to_vp && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 border-l-3 border-blue-500 text-sm text-blue-900 dark:text-blue-200 rounded">
                      <strong>Note de l'administrateur :</strong> {r.admin_note_to_vp}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <button
                    className="btn-secondary"
                    onClick={() => openReport(r)}
                    data-testid={`open-report-${r.report_id}`}
                  >
                    <FileText size={14} /> Voir détails
                  </button>
                  <button
                    className="btn-secondary !border-red-500 text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                    onClick={() => { setDecisionCtx({ report: r, decision: "rejected" }); setNote(""); }}
                    disabled={acting === r.report_id}
                    data-testid={`vp-reject-${r.report_id}`}
                  >
                    <XCircle size={14} /> Refuser
                  </button>
                  <button
                    className="btn-primary !bg-green-600 hover:!bg-green-700"
                    onClick={() => { setDecisionCtx({ report: r, decision: "approved" }); setNote(""); }}
                    disabled={acting === r.report_id}
                    data-testid={`vp-approve-${r.report_id}`}
                  >
                    <CheckCircle2 size={14} /> Approuver
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail viewer */}
      {open && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white dark:bg-neutral-900 border-2 border-[#0B0F19] dark:border-white w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-200 dark:border-neutral-700 sticky top-0 bg-white dark:bg-neutral-900 z-10">
              <p className="overline">Détails du rapport</p>
              <div className="font-heading font-black text-2xl tracking-tight">{open.name}</div>
              <div className="text-sm text-gray-500 mt-1 flex flex-wrap gap-3">
                <span><User size={12} className="inline mr-1" />{open.owner?.name}</span>
                <span><Calendar size={12} className="inline mr-1" />{open.month || "—"}</span>
                <span><FileText size={12} className="inline mr-1" />{open.invoices?.length || 0} facture(s)</span>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {open.loading ? (
                <div className="text-center py-12 text-gray-400">Chargement...</div>
              ) : (
                <>
                  <button className="btn-secondary" onClick={() => downloadPdf(open)} data-testid="vp-download-pdf">
                    <FileText size={14} /> Télécharger le PDF complet
                  </button>
                  <div className="border-t border-gray-200 dark:border-neutral-700 pt-4">
                    <h3 className="font-heading font-black text-lg mb-3">Liste des factures</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-gray-500 border-b border-gray-200 dark:border-neutral-700">
                            <th className="py-2">Date</th>
                            <th className="py-2">Établissement</th>
                            <th className="py-2">Catégorie</th>
                            <th className="py-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(open.invoices || []).map((inv) => (
                            <tr key={inv.invoice_id} className="border-b border-gray-100 dark:border-neutral-800">
                              <td className="py-2">{inv.date || "—"}</td>
                              <td className="py-2 font-medium">{inv.merchant || inv.description || "—"}</td>
                              <td className="py-2 text-xs text-gray-500">{inv.category || "—"}</td>
                              <td className="py-2 text-right num font-semibold">{(inv.total || 0).toFixed(2)} $</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="font-bold border-t-2 border-gray-900 dark:border-white">
                            <td colSpan="3" className="py-3"><DollarSign size={14} className="inline" /> Total</td>
                            <td className="py-3 text-right num">{(open.invoices || []).reduce((s, i) => s + (i.total || 0), 0).toFixed(2)} $</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="p-5 border-t border-gray-200 dark:border-neutral-700 flex gap-2 justify-end sticky bottom-0 bg-white dark:bg-neutral-900">
              <button className="btn-secondary" onClick={() => setOpen(null)}>Fermer</button>
              <button
                className="btn-secondary !border-red-500 text-red-700"
                onClick={() => { setDecisionCtx({ report: open, decision: "rejected" }); setNote(""); }}
              >
                <XCircle size={14} /> Refuser
              </button>
              <button
                className="btn-primary !bg-green-600 hover:!bg-green-700"
                onClick={() => { setDecisionCtx({ report: open, decision: "approved" }); setNote(""); }}
              >
                <CheckCircle2 size={14} /> Approuver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decision modal */}
      {decisionCtx && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="vp-decision-modal">
          <div className="bg-white dark:bg-neutral-900 border-2 border-[#0B0F19] dark:border-white w-full max-w-md">
            <div className="p-5 border-b border-gray-200 dark:border-neutral-700">
              <p className="overline">{decisionCtx.decision === "approved" ? "Approuver" : "Refuser"} le rapport</p>
              <div className="font-heading font-black text-xl tracking-tight">{decisionCtx.report.name}</div>
              <div className="text-sm text-gray-500 mt-1">{decisionCtx.report.owner_name}</div>
            </div>
            <div className="p-5">
              <label className="ra-label">
                Note {decisionCtx.decision === "rejected" ? <span className="text-red-500">*</span> : <span className="text-gray-400 text-xs">(optionnel)</span>}
              </label>
              <textarea
                className="ra-input min-h-[120px]"
                placeholder={decisionCtx.decision === "rejected"
                  ? "Expliquez la raison du refus pour aider l'employé à corriger."
                  : "Commentaire optionnel..."}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="vp-decision-note"
              />
              <p className="text-xs text-gray-500 mt-2">L'administrateur recevra un courriel avec votre décision{decisionCtx.decision === "rejected" ? " et la note explicative" : ""}.</p>
            </div>
            <div className="p-5 border-t border-gray-200 dark:border-neutral-700 flex gap-2 justify-end">
              <button className="btn-secondary" onClick={() => { setDecisionCtx(null); setNote(""); }}>Annuler</button>
              <button
                className={decisionCtx.decision === "approved" ? "btn-primary !bg-green-600 hover:!bg-green-700" : "btn-primary !bg-red-600 hover:!bg-red-700"}
                onClick={decide}
                disabled={acting === decisionCtx.report.report_id || (decisionCtx.decision === "rejected" && !note.trim())}
                data-testid="confirm-vp-decision"
              >
                {decisionCtx.decision === "approved" ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                {decisionCtx.decision === "approved" ? " Confirmer l'approbation" : " Confirmer le refus"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
